import React, { useEffect, useState } from 'react';
import { 
    Container, Box, Grid, Typography, Paper, 
    TextField, InputAdornment, Button, Skeleton,
    Avatar, Chip, FormControl, InputLabel, Select, MenuItem,
    RadioGroup, FormControlLabel, Radio, Divider, Drawer, Card, CardMedia, CardContent, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useParams, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faStar, faMapMarkerAlt, faTimes } from '@fortawesome/free-solid-svg-icons';

import { GetMerchantsByCategoryAndLocation, GetToSearchMerchants } from '../../../controllers/MerchantsController'; 
import ReadOnlyContent from '../../../components/ReadOnlyContent';

export default function SearchPage() {
    const { category: paramCategory, location: paramLocation } = useParams();
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // --- ESTADOS ---
    const [filters, setFilters] = useState({
        country: '', 
        city: paramLocation && paramLocation !== 'all' ? paramLocation : '',
        category: paramCategory && paramCategory !== 'all' ? paramCategory : '',
        search: ''
    });

    const [countriesList, setCountriesList] = useState([]); 
    const [citiesList, setCitiesList] = useState([]); 
    const [categoriesList, setCategoriesList] = useState([]);
    const [merchants, setMerchants] = useState([]);
    
    const [isLoading, setIsLoading] = useState(false);
    const [areFiltersLoading, setAreFiltersLoading] = useState(true);
    
    // Estado para abrir/cerrar filtros en móvil
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    // 1. CARGAR DATOS INICIALES
    useEffect(() => {
        fetchFilters();
    }, []);

    const fetchFilters = async () => {
        try {
            const response = await GetToSearchMerchants(); 
            if (response.success) {
                setCountriesList(response.data.locations);
                setCategoriesList(response.data.categories);
            }
        } catch (error) {
            console.error("Error cargando filtros:", error);
        }
        setAreFiltersLoading(false);
    };

    // 2. LÓGICA CASCADA PAÍS -> CIUDAD
    useEffect(() => {
        if (filters.country) {
            const selectedCountry = countriesList.find(c => c.nombre === filters.country);
            setCitiesList(selectedCountry ? selectedCountry.cities : []);
        } else {
            setCitiesList([]);
        }
    }, [filters.country, countriesList]);

    // 3. BUSCAR NEGOCIOS (DEBOUNCE)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchMerchants();
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [filters]);

    const fetchMerchants = async () => {
        setIsLoading(true);
        try {
            const response = await GetMerchantsByCategoryAndLocation(
                filters.category || 'all', 
                filters.city || 'all', 
                filters.search,
                filters.country || 'all'
            );
            if (response.success) {
                setMerchants(response.data.data || response.data); 
            }
        } catch (error) {
            console.error(error);
        }
        setIsLoading(false);
    };

    // --- HANDLERS ---
    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            if (key === 'country') return { ...prev, [key]: value, city: '' };
            return { ...prev, [key]: value };
        });
    };

    const clearFilters = () => {
        setFilters({ country: '', city: '', category: '', search: '' });
    };

    // --- COMPONENTE INTERNO: CONTENIDO DE FILTROS ---
    // (Extraído para usarlo en Sidebar Desktop y Drawer Móvil sin repetir código)
    const FilterContent = () => (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center">
                    <FontAwesomeIcon icon={faFilter} color="#173666" />
                    <Typography variant="h6" fontWeight="bold" ml={1} color="#173666">Filtros</Typography>
                </Box>
                <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none' }}>
                    Limpiar
                </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* Buscador */}
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Búsqueda</Typography>
            <TextField 
                fullWidth placeholder="Nombre, servicio..." variant="outlined" size="small"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><FontAwesomeIcon icon={faSearch} color="#ccc"/></InputAdornment> }}
            />

            {/* Ubicación */}
            {areFiltersLoading ? <Skeleton height={100} /> : (
                <Box display="flex" flexDirection="column" gap={2} mb={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>País</InputLabel>
                        <Select
                            value={filters.country}
                            label="País"
                            onChange={(e) => handleFilterChange('country', e.target.value)}
                        >
                            <MenuItem value=""><em>Todos</em></MenuItem>
                            {countriesList.map(c => (
                                <MenuItem key={c.id} value={c.nombre}>{c.nombre}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small" disabled={!filters.country}>
                        <InputLabel>Ciudad</InputLabel>
                        <Select
                            value={filters.city}
                            label="Ciudad"
                            onChange={(e) => handleFilterChange('city', e.target.value)}
                        >
                            <MenuItem value=""><em>Todas</em></MenuItem>
                            {citiesList.map(city => (
                                <MenuItem key={city.idCiudad} value={city.nombre}>{city.nombre}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* Categorías */}
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Categorías</Typography>
            <Box sx={{ maxHeight: '300px', overflowY: 'auto', pr: 1 }}>
                {areFiltersLoading ? <Skeleton height={150} /> : (
                    <RadioGroup
                        value={filters.category}
                        onChange={(e) => handleFilterChange('category', e.target.value)}
                    >
                        <FormControlLabel value="" control={<Radio size="small"/>} label={<Typography variant="body2">Todas</Typography>} />
                        {categoriesList.map(cat => (
                            <FormControlLabel 
                                key={cat.id} 
                                value={cat.nombre} 
                                control={<Radio size="small"/>} 
                                label={<Typography variant="body2">{cat.nombre}</Typography>} 
                            />
                        ))}
                    </RadioGroup>
                )}
            </Box>
            
            {/* Botón cerrar solo para móvil */}
            {isMobile && (
                <Button 
                    fullWidth variant="contained" 
                    onClick={() => setMobileFiltersOpen(false)}
                    sx={{ mt: 4, py: 1.5, borderRadius: 2 }}
                >
                    Ver Resultados
                </Button>
            )}
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#fff', pt: { xs: 10, md: 12, lg:2 }, pb: 5 }}>
            <Container maxWidth="xl">
                
                {/* --- BOTÓN DE FILTROS MÓVIL (Solo visible en XS/SM) --- */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 3, justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold" color="#173666">
                        Resultados ({merchants.length})
                    </Typography>
                    <Button 
                        variant="outlined" 
                        startIcon={<FontAwesomeIcon icon={faFilter} />}
                        onClick={() => setMobileFiltersOpen(true)}
                        sx={{ borderRadius: 20, textTransform: 'none', bgcolor: '#fff' }}
                    >
                        Filtros
                    </Button>
                </Box>

                <Grid container spacing={4}>
                    
                    {/* --- COLUMNA 1: FILTROS DESKTOP (Oculto en móvil) --- */}
                    <Grid item md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Box sx={{ position: 'sticky', top: 100 }}>
                            <Paper elevation={0} sx={{ borderRadius: 4, bgcolor: '#fff', border: '1px solid #e0e0e0' }}>
                                <FilterContent />
                            </Paper>
                        </Box>
                    </Grid>

                    {/* --- DRAWER MÓVIL (Off-canvas menu) --- */}
                    <Drawer
                        anchor="left"
                        open={mobileFiltersOpen}
                        onClose={() => setMobileFiltersOpen(false)}
                        PaperProps={{ sx: { width: '85%', maxWidth: 360 } }}
                    >
                        <FilterContent />
                    </Drawer>

                    {/* --- COLUMNA 2: RESULTADOS --- */}
                    <Grid item xs={12} md={9}>
                        {/* Título solo en Desktop */}
                        <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 3 }}>
                            <Typography variant="h5" fontWeight="bold" color="#173666">
                                {filters.category ? filters.category : 'Explorar Negocios'} 
                                <Typography component="span" variant="body2" color="text.secondary" ml={2}>
                                    ({merchants.length} encontrados)
                                </Typography>
                            </Typography>
                        </Box>

                        <Grid container spacing={3}>
                            {isLoading ? (
                                [1, 2, 3, 4, 5, 6].map(n => (
                                    <Grid item xs={12} sm={6} lg={4} key={n}>
                                        <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 4 }} />
                                    </Grid>
                                ))
                            ) : merchants.length > 0 ? (
                                merchants.map(item => (
                                    <Grid item xs={12} sm={6} lg={4} key={item.uuid}>
                                        {/* --- NUEVA TARJETA VERTICAL CON LOGO FLOTANTE --- */}
                                        <Card 
                                            elevation={0}
                                            onClick={() => history.push('/merchants/' + item.uuid + '/profile')}
                                            sx={{
                                                borderRadius: 4,
                                                bgcolor: '#fff',
                                                border: '1px solid #eee',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                position: 'relative',
                                                overflow: 'visible', // Importante para que el logo sobresalga si quisieras, pero aqui lo contenemos
                                                '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 12px 24px rgba(0,0,0,0.1)' }
                                            }}
                                        >
                                            {/* 1. Imagen de Portada (Cover) */}
                                            <Box sx={{ position: 'relative' }}>
                                                <CardMedia
                                                    component="img"
                                                    height="160"
                                                    image={item.cover_image || '/assets/default-cover.jpg'}
                                                    alt={item.merchant_name}
                                                    sx={{ 
                                                        borderTopLeftRadius: 16, 
                                                        borderTopRightRadius: 16,
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                                {/* Chip de Categoría sobre la imagen */}
                                                <Chip 
                                                    label={item.category} 
                                                    size="small" 
                                                    sx={{ 
                                                        position: 'absolute', top: 12, right: 12, 
                                                        bgcolor: 'rgba(255,255,255,0.95)', 
                                                        fontWeight: 'bold', color: '#173666',
                                                        backdropFilter: 'blur(4px)'
                                                    }} 
                                                />
                                            </Box>

                                            {/* 2. Logo Flotante (Highlight solicitado) */}
                                            <Box sx={{ px: 2, mt: -4, position: 'relative', zIndex: 2 }}>
                                                <Avatar 
                                                    src={item.profile_image} 
                                                    alt={item.merchant_name}
                                                    sx={{ 
                                                        width: 70, height: 70, 
                                                        border: '4px solid #fff',
                                                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                                                        bgcolor: '#fff'
                                                    }} 
                                                />
                                            </Box>

                                            {/* 3. Contenido */}
                                            <CardContent sx={{ pt: 1, pb: '16px !important' }}>
                                                {/* Nombre */}
                                                <Typography variant="h6" fontWeight="bold" noWrap title={item.merchant_name}>
                                                    {item.merchant_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    <FontAwesomeIcon icon={faMapMarkerAlt}/> {item.city}
                                                </Typography>

                                                {/* Rating & Ciudad */}
                                                <Box display="flex" alignItems="center" mt={0.5} mb={2}>
                                                    <FontAwesomeIcon icon={faStar} color="#fcba03" size="sm" />
                                                    <Typography variant="body2" fontWeight="bold" ml={0.5} mr={1}>
                                                        {item.rating} <span style={{fontWeight:'normal'}}>({item.reviews_count} reviews)</span>
                                                    </Typography>
                                                    
                                                </Box>

                                                <Divider sx={{ mb: 2 }} />

                                                {/* Descripción corta */}
                                                <Box sx={{ 
                                                    height: 40, 
                                                    overflow: 'hidden', 
                                                    display: '-webkit-box', 
                                                    WebkitLineClamp: 2, 
                                                    WebkitBoxOrient: 'vertical',
                                                    fontSize: '0.875rem',
                                                    color: 'text.secondary'
                                                }}>
                                                    <div style={{ pointerEvents: 'none' }}>
                                                        <ReadOnlyContent htmlContent={item.description} />
                                                    </div>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12}>
                                    <Box textAlign="center" py={5} bgcolor="#fff" borderRadius={4} border="1px dashed #e0e0e0">
                                        <Typography color="text.secondary">
                                            No se encontraron resultados.
                                        </Typography>
                                        <Button onClick={clearFilters} sx={{ mt: 1 }}>
                                            Limpiar filtros
                                        </Button>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}